import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import gsap from 'gsap';
import Loading from '../components/Loading';
import { useGetRouletteCurrentRoundQuery, useGetRouletteResultsQuery, useGetRouletteInplayBetsQuery } from '../services/roulette/rouletteApi';
import { getResolvedWsBaseUrl } from '../utils/domainConfig';

// ── European wheel constants ─────────────────────────────────────────────────
const WHEEL_ORDER = [0,32,15,19,4,21,2,25,17,34,6,27,13,36,11,30,8,23,10,5,24,16,33,1,20,14,31,9,22,18,29,7,28,12,35,3,26];
const RED_SET     = new Set([1,3,5,7,9,12,14,16,18,19,21,23,25,27,30,32,34,36]);
const SECTOR_DEG  = 360 / WHEEL_ORDER.length;
const HALF_RAD    = (SECTOR_DEG / 2) * (Math.PI / 180);
const R           = 140;
const TEXT_R      = 108;
const INNER_R     = 32;

const LX = -(R * Math.sin(HALF_RAD));
const LY = -(R * Math.cos(HALF_RAD));
const RX =  R * Math.sin(HALF_RAD);
const RY = -(R * Math.cos(HALF_RAD));
const SECTOR_PATH = `M0,0 L${LX.toFixed(2)},${LY.toFixed(2)} A${R},${R} 0 0 1 ${RX.toFixed(2)},${RY.toFixed(2)} Z`;

const numColor = (n) => n === 0 ? '#1e8c3a' : RED_SET.has(n) ? '#c0392b' : '#0f0f0f';

function RouletteWheel({ resultNum, wheelRef }) {
  return (
    <svg
      ref={wheelRef}
      viewBox="0 0 300 300"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: '280px', height: '280px', filter: 'drop-shadow(0 0 12px rgba(244,192,66,0.5))' }}
    >
      <circle cx="150" cy="150" r={R + 7} fill="#8B6914" />
      <circle cx="150" cy="150" r={R + 4} fill="#C9A227" />
      <circle cx="150" cy="150" r={R + 1} fill="#8B6914" />
      <g transform="translate(150,150)">
        {WHEEL_ORDER.map((num, i) => {
          const rotation = i * SECTOR_DEG;
          const isResult = resultNum !== null && resultNum !== undefined && num === resultNum;
          return (
            <g key={num} transform={`rotate(${rotation})`}>
              <g style={{
                transform: isResult ? 'scale(1.1)' : 'scale(1)',
                transformOrigin: '0 0',
                transition: 'transform 0.4s ease, filter 0.4s ease',
                filter: isResult
                  ? 'brightness(1.8) drop-shadow(0 0 5px rgba(255,215,0,0.95))'
                  : 'none',
              }}>
                <path d={SECTOR_PATH} fill={isResult ? '#ffd700' : numColor(num)} stroke="#C9A227" strokeWidth="0.6" />
                <text x="0" y={-TEXT_R} textAnchor="middle" dominantBaseline="middle"
                  fontSize="6.5" fontWeight="bold" fill={isResult ? '#000' : '#fff'}
                  style={{ userSelect: 'none' }}>
                  {num}
                </text>
              </g>
            </g>
          );
        })}
        {WHEEL_ORDER.map((_, i) => {
          const angle = i * SECTOR_DEG * Math.PI / 180;
          return (
            <circle key={`s${i}`} cx={(R - 4) * Math.sin(angle)} cy={-(R - 4) * Math.cos(angle)} r="1.5" fill="#C9A227" />
          );
        })}
        <circle cx="0" cy="0" r={INNER_R + 8} fill="#2a2a0a" stroke="#8B6914" strokeWidth="1.5" />
        <circle cx="0" cy="0" r={INNER_R + 2} fill="#1a1a0a" stroke="#C9A227" strokeWidth="1" />
        <circle cx="0" cy="0" r={INNER_R - 2} fill="#C9A227" stroke="#8B6914" strokeWidth="1" />
        <circle cx="0" cy="0" r={INNER_R - 8} fill="#1a1a0a" />
        <circle cx="0" cy="0" r="8" fill="#C9A227" />
        <circle cx="0" cy="0" r="5" fill="#fff8e1" />
        <circle cx="0" cy="0" r="2" fill="#8B6914" />
      </g>
    </svg>
  );
}

const THEME = 'var(--primary)';

const formatTime = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
};

const RouletteLivePage = () => {
  const navigate   = useNavigate();
  useParams();
  const { user, accessToken } = useSelector((state) => state.auth);

  const [countdown,  setCountdown]  = useState(0);
  const [wsResult,   setWsResult]   = useState(null);
  const [wsHistory,  setWsHistory]  = useState([]);
  const [wsRoundId,  setWsRoundId]  = useState('');
  const [phase,      setPhase]      = useState('betting');

  const wsRef         = useRef(null);
  const cdRef         = useRef(null);
  const wheelRef      = useRef(null);

  const gsapTlRef     = useRef(null);
  const resultTimeRef = useRef(0);

  const token  = accessToken ?? '';
  const userId = user?.id || user?.userId || '';

  const { data: currentRound } = useGetRouletteCurrentRoundQuery(undefined, { pollingInterval: 5000 });
  const { data: resultsData }  = useGetRouletteResultsQuery({ limit: 15 }, { pollingInterval: 10000 });
  const { data: betsData, isLoading: betsLoading } = useGetRouletteInplayBetsQuery(
    undefined,
    { pollingInterval: 3000 }
  );

  const roundId    = wsRoundId || currentRound?.data?.roundId || currentRound?.roundId || '—';
  const bets       = betsData?.data || betsData?.bets || [];
  const apiHistory = resultsData?.data ?? resultsData?.results ?? [];
  const lastResult = wsResult
    || (wsHistory.length > 0 ? wsHistory[0] : null)
    || (apiHistory.length > 0 ? apiHistory[0] : null);


  // WebSocket
  useEffect(() => {
    const wsBase = getResolvedWsBaseUrl(import.meta.env.VITE_WS_URL || 'wss://api.sky99.co');
    const url    = `${wsBase}/api/roulette/ws?userId=${userId}&token=${token}`;

    const startCountdown = (secs) => {
      if (cdRef.current) clearInterval(cdRef.current);
      setCountdown(secs);
      let t = secs;
      cdRef.current = setInterval(() => {
        t -= 1;
        if (t <= 0) { clearInterval(cdRef.current); setCountdown(0); }
        else setCountdown(t);
      }, 1000);
    };

    const connect = () => {
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const msg  = JSON.parse(ev.data);
          const type = msg.type ?? msg.event;
          if (type === 'init') {
            if (msg.roundId) setWsRoundId(msg.roundId);
            if (Array.isArray(msg.lastResults)) setWsHistory(msg.lastResults);
            if (msg.result != null) setWsResult({ result: msg.result, color: msg.color ?? 'black' });
            const secs = parseInt(msg.autotime ?? '0');
            if (secs > 0) startCountdown(secs); else setCountdown(0);
            setPhase(msg.betOpen === true ? 'betting' : 'result');
          }
          if (type === 'newRound' || type === 'round_start') {
            if (msg.roundId) setWsRoundId(msg.roundId);
            if (cdRef.current) clearInterval(cdRef.current);
          }
          if (type === 'tick' || type === 'countdown') {
            const secs = parseInt(msg.autotime ?? msg.timeLeft ?? msg.data?.timeLeft ?? '0');
            if (msg.betOpen === true || type === 'countdown') {
              const elapsed = Date.now() - resultTimeRef.current;
              setTimeout(() => { startCountdown(secs); setPhase('betting'); }, Math.max(0, 5500 - elapsed));
            }
          }
          if (type === 'spinning') {
            if (msg.roundId) setWsRoundId(msg.roundId);
            setWsResult(null);
            setPhase('spinning');
            if (cdRef.current) clearInterval(cdRef.current);
            const secs = parseInt(msg.spinningLeft ?? '0');
            if (secs > 0) startCountdown(secs); else setCountdown(0);
          }
          if (type === 'result' || type === 'betResult') {
            if (msg.result != null) {
              const entry = { result: msg.result, color: msg.color ?? 'black' };
              setWsResult(entry);
              setWsHistory(prev => [entry, ...prev].slice(0, 20));
            }
            resultTimeRef.current = Date.now();
            setPhase('result');
            if (cdRef.current) clearInterval(cdRef.current);
            setCountdown(0);
          }
        } catch { /* ignore */ }
      };
      ws.onerror  = () => {};
      ws.onclose  = () => { setTimeout(connect, 3000); };
    };

    connect();
    return () => {
      if (cdRef.current) clearInterval(cdRef.current);
      if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); }
    };
  }, [userId, token]);

  // Cleanup GSAP on unmount
  useEffect(() => {
    return () => {
      if (gsapTlRef.current) gsapTlRef.current.kill();
      gsap.killTweensOf([wheelRef.current]);
    };
  }, []);

  // Animation
  useEffect(() => {
    const el = wheelRef.current;
    if (!el) return;

    if (phase === 'betting') {
      if (gsapTlRef.current) { gsapTlRef.current.kill(); gsapTlRef.current = null; }
      gsap.killTweensOf([el]);
      return;
    }

    if (phase === 'spinning') {
      if (gsapTlRef.current) { gsapTlRef.current.kill(); gsapTlRef.current = null; }
      const wRot = gsap.getProperty(el, 'rotation') || 0;
      gsap.killTweensOf([el]);
      gsap.to(el, { rotation: wRot + 360 * 100, duration: 200, ease: 'none', overwrite: true });
      return;
    }

    if (phase === 'result' && wsResult != null) {
      if (gsapTlRef.current) { gsapTlRef.current.kill(); gsapTlRef.current = null; }
      const wRot = gsap.getProperty(el, 'rotation') || 0;

      const idx    = WHEEL_ORDER.indexOf(wsResult.result);
      const tMod   = (360 - (idx * SECTOR_DEG % 360)) % 360;
      const cMod   = ((wRot % 360) + 360) % 360;
      let   dWheel = tMod - cMod;
      if (dWheel <= 0) dWheel += 360;
      const wheelFinal = wRot + dWheel;

      const tl = gsap.timeline();
      gsapTlRef.current = tl;
      tl.to(el, { rotation: wheelFinal, duration: 3, ease: 'power3.out', overwrite: true }, 0);
    }
  }, [phase, wsResult]);

  const phaseLabel = phase === 'betting' ? 'Betting Open' : phase === 'spinning' ? 'Spinning…' : 'Result';
  const phaseColor = phase === 'betting' ? '#16a34a' : phase === 'spinning' ? '#dc2626' : THEME;


  return (
    <div className="w-full">
      <div className="px-5 py-3 flex items-center justify-between mb-4" style={{ background: THEME }}>
        <div>
          <span className="text-[#1a1a2e] font-semibold text-base">Roulette — Live</span>
          <div className="text-xs text-[#5a4a1e] font-mono mt-0.5">{roundId}</div>
        </div>
        <button onClick={() => navigate(-1)}
          className="bg-white text-gray-700 px-4 py-1.5 hover:bg-gray-100 transition-colors text-sm">
          Back
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 px-2 items-start">

        {/* Left — Wheel */}
        <div className="w-full lg:w-1/2 rounded shadow border border-gray-200 overflow-hidden" style={{ background: '#ffffff' }}>
          <div className="flex items-center justify-between px-4 py-2"
            style={{ background: '#f7f7f8', borderBottom: '1px solid #e5e5e5' }}>
            <span className="text-xs font-bold px-3 py-1 rounded" style={{ background: phaseColor, color: '#fff' }}>
              {phaseLabel}
            </span>
            {countdown > 0 && (
              <span className="text-3xl font-black tabular-nums" style={{ color: phaseColor }}>
                {countdown}
              </span>
            )}
          </div>

          <div className="flex flex-col items-center py-6 gap-5">
            <div style={{ position: 'relative', width: '280px', height: '280px' }}>
              <div style={{
                position: 'absolute', inset: '-16px', borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(244,192,66,0.30) 0%, transparent 70%)',
                animation: 'rl-glow-pulse 2s ease-in-out infinite', pointerEvents: 'none',
              }} />
              <div style={{
                position: 'absolute', top: '-6px', left: '50%', transform: 'translateX(-50%)',
                width: 0, height: 0,
                borderLeft: '8px solid transparent', borderRight: '8px solid transparent',
                borderTop: '18px solid #ffd700',
                filter: 'drop-shadow(0 0 6px rgba(255,215,0,0.9))',
                zIndex: 10,
              }} />

              <RouletteWheel resultNum={phase === 'result' ? (lastResult?.result ?? null) : null} wheelRef={wheelRef} />

{phase === 'result' && lastResult != null && (
                <div style={{
                  position: 'absolute', inset: 0, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  pointerEvents: 'none', zIndex: 20,
                }}>
                  <span style={{
                    fontSize: '56px', fontWeight: 900, lineHeight: 1,
                    color: lastResult.color === 'red' ? '#dc2626' : lastResult.color === 'green' ? '#16a34a' : '#1a1a2e',
                    textShadow: '0 0 20px rgba(244,192,66,0.9), 0 0 40px rgba(244,192,66,0.5)',
                  }}>
                    {lastResult.result}
                  </span>
                </div>
              )}
            </div>


            {apiHistory.length > 0 && (
              <div className="flex gap-1.5 flex-wrap justify-center px-4">
                <div style={{
                  width: '100%', background: '#f7f7f8', padding: '8px 0',
                  borderRadius: 5, textAlign: 'center', marginBottom: 8,
                  fontSize: 13, fontWeight: 600, color: '#1a1a2e',
                }}>
                  Last Results
                </div>
                {apiHistory.slice(0, 15).map((h, i) => (
                  <span key={i} style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: '34px', height: '34px', borderRadius: '50%',
                    fontSize: '13px', fontWeight: 700, color: '#fff',
                    background: h.color === 'red'
                      ? 'linear-gradient(135deg, #e85555 0%, #c0392b 100%)'
                      : h.color === 'green'
                      ? 'linear-gradient(135deg, #27ae60 0%, #1e8c3a 100%)'
                      : 'linear-gradient(135deg, #3a3a3a 0%, #1a1a1a 100%)',
                    border: i === 0 ? '2px solid #ffd700' : '2px solid rgba(0,0,0,0.12)',
                    boxShadow: h.color === 'red' ? '0 4px 12px rgba(192,57,43,0.45)'
                      : h.color === 'green' ? '0 4px 12px rgba(30,140,58,0.45)'
                      : '0 4px 12px rgba(0,0,0,0.5)',
                  }}>
                    {h.result}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right — Bets table */}
        <div className="w-full lg:w-1/2 bg-white rounded shadow border border-gray-200 overflow-hidden">
          <div className="px-4 py-2.5 flex items-center justify-between" style={{ background: THEME }}>
            <span className="text-[#1a1a2e] font-semibold text-sm">Current Round Bets</span>
            <span className="text-xs text-[#5a4a1e]">{bets.length} bets</span>
          </div>

          <div className="overflow-x-auto">
            {betsLoading ? (
              <div className="flex justify-center py-8"><Loading /></div>
            ) : (
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-600 text-xs uppercase">
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">#</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">Client</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">Bet On</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">Type</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">Stake</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">P/L</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">Status</th>
                    <th className="px-3 py-2.5 text-left border-b border-gray-200">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {bets.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center text-gray-400">No bets for this round</td>
                    </tr>
                  ) : (
                    bets.map((bet, idx) => {
                      const pl = bet.pl ?? bet.profitLoss;
                      return (
                        <tr
                          key={bet._id || idx}
                          className={`border-l-4 ${
                            bet.status === 'won' ? 'border-l-green-500 bg-green-50/40'
                            : bet.status === 'lost' ? 'border-l-red-500 bg-red-50/40'
                            : 'border-l-yellow-400 bg-yellow-50/40'
                          }`}
                        >
                          <td className="px-3 py-2 border-b border-gray-100 text-gray-500">{idx + 1}</td>
                          <td className="px-3 py-2 border-b border-gray-100">
                            <div className="font-semibold text-gray-800 text-xs">{bet.userId || bet.username || '-'}</div>
                            {bet.fullName && <div className="text-[10px] text-gray-500">{bet.fullName}</div>}
                          </td>
                          <td className="px-3 py-2 border-b border-gray-100 font-medium text-gray-800">{bet.betOn ?? '-'}</td>
                          <td className="px-3 py-2 border-b border-gray-100 text-gray-600 capitalize">{bet.betType || '-'}</td>
                          <td className="px-3 py-2 border-b border-gray-100 text-gray-700">{bet.stake ?? '-'}</td>
                          <td className={`px-3 py-2 border-b border-gray-100 font-semibold text-xs ${(pl ?? 0) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                            {pl != null ? (pl >= 0 ? '+' : '') + Number(pl).toFixed(2) : '-'}
                          </td>
                          <td className="px-3 py-2 border-b border-gray-100">
                            <span className={`px-1.5 py-0.5 text-[10px] rounded font-medium capitalize border ${
                              bet.status === 'won'  ? 'bg-green-100 text-green-700 border-green-300'
                              : bet.status === 'lost' ? 'bg-red-100 text-red-700 border-red-300'
                              : 'bg-yellow-100 text-yellow-700 border-yellow-300'
                            }`}>
                              {bet.status || 'pending'}
                            </span>
                          </td>
                          <td className="px-3 py-2 border-b border-gray-100 text-gray-400 text-xs whitespace-nowrap">
                            {formatTime(bet.placedAt || bet.createdAt)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes rl-glow-pulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50%       { opacity: 1;   transform: scale(1.06); }
        }
      `}</style>
    </div>
  );
};

export default RouletteLivePage;
